import { NodeModel, RenderParams } from "@minoru/react-dnd-treeview";
import { ArrowRight } from "@mui/icons-material";
import { Typography } from "@mui/material";
import {
	FC,
	MouseEvent as ReactMouseEvent,
	useCallback,
	useEffect,
} from "react";

import { useConfigurationDelayCodeContext } from "../context/ConfigurationDelayCodeContext";
import { FileProperties } from "../models/FileProperties";
import { TypeIcon } from "./DelayConfiguration/TypeIcon";

const customStyles = `
  ul, ol {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }
`;

type Props = RenderParams & {
	node: NodeModel<FileProperties>;
	setContextMenuOver: (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => void;
	onDoubleClick: (
		data: NodeModel<FileProperties>,
		e: ReactMouseEvent<HTMLElement>,
	) => void;
	isSelected: boolean;
	isDragging: boolean;
	testIdPrefix?: string;
	onClick: (e: ReactMouseEvent, node: NodeModel<FileProperties>) => void;
	"data-node-id"?: string;
};
export const CustomNode: FC<Props> = ({
	setContextMenuOver,
	onDoubleClick,
	testIdPrefix = "",
	isSelected,
	...props
}) => {
	const {
		state: { openDelays },
		actions: { setOpenDelays },
	} = useConfigurationDelayCodeContext();

	const { id, droppable, data } = props.node;
	const indent = props.depth * 24;

	const handleClick = (e: ReactMouseEvent) => {
		props.onClick(e, props.node);
	};

	const handleToggle = (e: ReactMouseEvent) => {
		e.stopPropagation();
		if (openDelays[id]) {
			const newOpenDelays = { ...openDelays };
			delete newOpenDelays[id as number];
			setOpenDelays(newOpenDelays);
		} else {
			setOpenDelays({ ...openDelays, [id]: id });
		}
		props.onToggle();
	};

	const handleContextMenu = useCallback(
		(ev: any) => {
			ev.stopPropagation();
			ev.preventDefault();
			setContextMenuOver(props.node, ev);
		},
		[id, setContextMenuOver],
	);

	const handleViewDelayDialog = useCallback(
		(e: any) => {
			e.stopPropagation();
			e.preventDefault();
			onDoubleClick(props.node, e);
			setContextMenuOver(
				{
					id: "",
					parent: "",
					text: "",
					droppable: false,
				},
				e,
			);
		},
		[id, onDoubleClick],
	);

	useEffect(() => {
		props.containerRef.current?.addEventListener(
			"contextmenu",
			handleContextMenu,
		);
		props.containerRef.current?.addEventListener(
			"dblclick",
			handleViewDelayDialog,
		);

		return () => {
			props.containerRef.current?.removeEventListener(
				"contextmenu",
				handleContextMenu,
			);
			props.containerRef.current?.removeEventListener(
				"dblclick",
				handleViewDelayDialog,
			);
		};
	}, []);

	return (
		<div
			style={{
				paddingInlineStart: indent,
				alignItems: "center",
				display: "flex",
				height: "32px",
				listStyleType: "none",
				backgroundColor: isSelected ? "#e8f0fe" : "",
			}}
			data-node-id={id}
			onClick={handleClick}
			title={`${props.node.id} - ${props.node.text}`}
		>
			<style>{customStyles}</style>
			{props.node.droppable ? (
				<div
					style={{
						alignItems: "center",
						cursor: "pointer",
						display: "flex",
						height: "24px",
						justifyContent: "center",
						width: "24px",
						transition: "transform linear 0.1s",
						transform: props.isOpen ? "rotate(90deg)" : "rotate(0deg)",
					}}
				>
					<div onClick={handleToggle}>
						<ArrowRight data-testid={`arrow-right-icon-${id}`} />
					</div>
				</div>
			) : null}
			<div
				style={{
					display: "flex",
				}}
			>
				<TypeIcon droppable={droppable as boolean} fileType={data?.fileType} />
			</div>
			<div
				style={{
					paddingInlineStart: "8px",
				}}
			>
				<Typography variant="body2">{props.node.text}</Typography>
			</div>
		</div>
	);
};
